import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';

/**
 * Pantalla de "estamos actualizando".
 *
 * Aparece cuando el motor deja de contestar porque lo estamos reemplazando —un
 * despliegue— y se retira sola en cuanto vuelve. Antes de esto, esos segundos se
 * le mostraban al usuario como un error crudo del navegador por algo que no era
 * un error.
 *
 * Cómo se enciende: `apiFetch` avisa por el evento global `opus:en-mantenimiento`
 * al recibir el aviso que arma el nginx de esta pantalla cuando no logra hablarle
 * al motor (ver infra/nginx/nginx.conf → `@motor_ausente`). Basta con que UNA
 * llamada cualquiera de la aplicación se tope con eso.
 *
 * Cómo se apaga: preguntando por el latido del motor cada pocos segundos. Se pide
 * MÁS DE UNA respuesta buena seguida a propósito: durante el despliegue el motor
 * puede contestar un instante y volver a irse, y una sola respuesta buena haría
 * parpadear la pantalla.
 *
 * Va montada por encima de todo (fuera del arranque de sesión): si alguien abre
 * la aplicación justo en la ventana del despliegue, lo que ve es este aviso y no
 * la pantalla de inicio de sesión —que es donde caía, porque preguntar quién es
 * también falla—.
 */

const LATIDO_URL = '/api/v1/latido';
/** Cada cuánto se pregunta si el motor volvió. */
const INTERVALO_MS = 3_000;
/** Respuestas buenas seguidas que se exigen para dar el motor por vuelto. */
const LATIDOS_BUENOS_SEGUIDOS = 2;

const useStyles = makeStyles({
  telon: {
    position: 'fixed',
    inset: 0,
    zIndex: 9_000,
    backgroundColor: 'var(--bg-page)',
    display: 'grid',
    placeItems: 'center',
    padding: 'var(--space-xxl)',
    boxSizing: 'border-box',
  },
  caja: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-l)',
    maxWidth: '440px',
    textAlign: 'center',
  },
  titulo: {
    margin: 0,
    fontSize: 'var(--fs-600)',
    lineHeight: 'var(--lh-600)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  cuerpo: {
    margin: 0,
    fontSize: 'var(--fs-400)',
    lineHeight: 'var(--lh-400)',
    color: 'var(--text-secondary)',
  },
  pie: {
    margin: 0,
    fontSize: 'var(--fs-200)',
    lineHeight: 'var(--lh-200)',
    color: 'var(--text-tertiary)',
  },
});

export function MantenimientoGate() {
  const styles = useStyles();
  const [visible, setVisible] = useState(false);
  const buenosSeguidos = useRef(0);

  useEffect(() => {
    function onMantenimiento() {
      setVisible((yaVisible) => {
        if (!yaVisible) buenosSeguidos.current = 0;
        return true;
      });
    }
    window.addEventListener('opus:en-mantenimiento', onMantenimiento);
    return () => window.removeEventListener('opus:en-mantenimiento', onMantenimiento);
  }, []);

  /**
   * Al volver el motor se RECARGA la aplicación, no se esconde la pantalla y listo.
   *
   * Se probó lo otro —esconderla y refrescar las consultas— y se vio fallar en el
   * navegador contra la imagen real: si el usuario ABRIÓ una pantalla mientras el motor
   * no estaba, esa pantalla se guardó el error en su propio estado, que no es una
   * consulta de TanStack y por lo tanto refrescar no lo cura. La pantalla de
   * "estamos actualizando" se retiraba dejando debajo una página muerta, en rojo, con
   * el aviso de mantenimiento convertido en mensaje de error. Peor que no hacer nada.
   *
   * Recargar es lo único que sirve para cualquier pantalla sin ir a parchear una por
   * una. Se conserva la dirección (el usuario vuelve a donde estaba) y la sesión; se
   * pierde lo transitorio (a medio escribir, el scroll). En una ventana de ~25 s es un
   * intercambio que conviene.
   */
  const alVolver = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelado = false;

    async function preguntar() {
      let vive = false;
      try {
        // `cache: 'no-store'` para que el navegador no nos conteste con una
        // respuesta guardada y demos por vuelto un motor que sigue abajo.
        const res = await fetch(LATIDO_URL, { cache: 'no-store', credentials: 'include' });
        vive = res.ok;
      } catch {
        vive = false; // Ni siquiera se pudo preguntar: sigue abajo.
      }
      if (cancelado) return;

      buenosSeguidos.current = vive ? buenosSeguidos.current + 1 : 0;
      if (buenosSeguidos.current >= LATIDOS_BUENOS_SEGUIDOS) alVolver();
    }

    const id = window.setInterval(() => void preguntar(), INTERVALO_MS);
    void preguntar();
    return () => {
      cancelado = true;
      window.clearInterval(id);
    };
  }, [visible, alVolver]);

  if (!visible) return null;

  return (
    <div className={styles.telon} role="alertdialog" aria-live="assertive" aria-label={`Estamos actualizando ${nombreProductoActivo()}`}>
      <div className={styles.caja}>
        <Spinner size="huge" />
        <h1 className={styles.titulo}>Estamos actualizando {nombreProductoActivo()}</h1>
        <p className={styles.cuerpo}>
          Tardamos unos minutos. Tu trabajo quedó guardado y esta pantalla se cierra
          sola en cuanto terminemos.
        </p>
        <p className={styles.pie}>No cierres la ventana.</p>
      </div>
    </div>
  );
}
